import moment from "moment-timezone";
import AxiosClient from "../../API/AxiosClient";
import { isSystemPool } from "../../BusinessUtils";
import { arraysAreEqualAfterSort, hasDatesChanged } from "../../Helper";
import { resvTypeByLid } from "../../selectors/Selectors";
import * as types from "../actions/types";
import { setRequestsLoading, setRequestsLoadingByResvType } from "./axiosStatus";
import { setCartProductExtras } from "./checkoutCart";
import { resetLastSearchedTimestampByResvtype, setLastSearchedDateSpan, updateLastSearchedTimestamp } from "./search";
import axios from "axios";

export const fetchReservationTypeResultsFailed = (error, resvtype) => {
    return {
        type: types.FETCH_RESERVATIONTYPERESULT_FAILED,
        error: error,
        resvtype: resvtype,
    };
};

export const setResultSetId = (payload, resvtype) => {
    return {
        type: types.SET_RESULTSETID,
        payload: payload,
        resvtype: resvtype,
    };
};

export const setReservationTypeResults = (
    payload,
    reservationType,
    promoCode,
    checkPriceAndAvailability,
    grouppoollid,
    pricecode,
    isInitialSearch,
    isLoggedIn,
    setPreFetch
) => {
    return {
        type: types.SET_RESERVATIONTYPERESULT,
        payload,
        reservationType,
        promoCode,
        checkPriceAndAvailability,
        grouppoollid,
        pricecode,
        isInitialSearch,
        isLoggedIn,
        setPreFetch,
    };
};

export const clearReservationTypeResults = resvtype => {
    return {
        type: types.CLEAR_RESERVATIONRESULTDATA,
        resvtype: resvtype,
    };
};

export const clearAllReservationTypeResults = () => {
    return {
        type: types.CLEAR_ALL_RESERVATIONRESULTDATA,
    };
};

export const removeReservationTypeResults = resvtype => {
    return {
        type: types.REMOVE_RESERVATIONRESULT,
        resvtype: resvtype,
    };
};

export const setSlowRequestParam = slowRequest => {
    return {
        type: types.SET_SLOW_REQUEST_PARAM,
        slowRequest: slowRequest,
    };
};

let controller;
let prevResvTypeLid;

export const fetchReservationTypeResults = (
    payload,
    checkPriceAndAvailability = true,
    isInitialSearch = false,
    removePromoCode
) => {
    const { isLoggedIn, useAutoSearch, doPreFetchOnly, preFetchResult, ...searchParams } = payload;

    // If searching without taking price into consideration. Will increase performance.
    if (!checkPriceAndAvailability) {
        searchParams.checkprice = false;
        searchParams.checkavail = false;
    }

    if (!searchParams.grouppoollid) {
        delete searchParams.grouppoollid;
    }

    const resvtypelid = parseInt(payload.resvtype);
    let promoCode = payload.promoCode;

    return async (dispatch, getState) => {
        const state = getState();
        const reservationType = resvTypeByLid(state, resvtypelid);

        const catchSearchErrors = error => {
            console.log("catchSearchErrors", error);
            // If the request was manually aborted, we do not want to handle the error.
            // During prefetch, we expect to manually abort the request if a new search is made.
            if (axios.isCancel(error)) {
                console.log("Request canceled", error.message);
                return;
            }

            if (error.response.status === 400) {
                if (error.response.data.errors.length) {
                    // If we have a blocked date we want to clear search results for this type
                    const blockedDateError = error.response.data.errors.find(problem => {
                        return problem.code === "BLOCKED_DATE";
                    });

                    if (blockedDateError && !doPreFetchOnly) {
                        dispatch(clearReservationTypeResults(resvtypelid));
                    }
                }
            }

            if (doPreFetchOnly) {
                dispatch(clearPreFetch(resvtypelid));

                dispatch(
                    setRequestsLoadingByResvType({
                        request: "preFetchReservationTypeResults",
                        resvTypeId: resvtypelid,
                        loading: false,
                    })
                );
            }

            dispatch(fetchReservationTypeResultsFailed(error, resvtypelid));

            dispatch(
                setRequestsLoadingByResvType({
                    request: "fetchReservationTypeResults",
                    resvTypeId: resvtypelid,
                    loading: false,
                })
            );
        };

        if (!reservationType) {
            throw new Error(`Cannot find reservation type by lid ${resvtypelid}`);
        }

        let forceNewSearch = false;
        let validatePromoCode = false;
        const changedValues = [];

        // Check if dates have been changed and trigger a reset/new search.
        if (typeof payload.oldStartDate !== "undefined" && typeof payload.oldEndDate !== "undefined") {
            const datesChanged = hasDatesChanged(
                payload.startdate,
                payload.enddate,
                payload.oldStartDate,
                payload.oldEndDate
            );

            if (datesChanged) {
                forceNewSearch = true;
                changedValues.push("dates");
            }
        }

        // Check if ages have been changed and trigger a reset/new search.
        if (payload.oldAges && payload.ages && !arraysAreEqualAfterSort(payload.oldAges, payload.ages)) {
            forceNewSearch = true;
            changedValues.push("ages");
        }

        // Check if promoCode have been changed and trigger a reset/new search.
        if (payload.oldPromoCode?.toLowerCase() !== promoCode?.toLowerCase()) {
            forceNewSearch = true;
            changedValues.push("promoCode");

            // If promoCode is set, we need to validate it before we can use it.
            if (promoCode) {
                validatePromoCode = true;
            }
        }

        // Check if grouppoollid have been changed and trigger a reset/new search.
        // Only force a new search if the old grouppoollid has a truthy value. If the old groupoollid is falsy,
        // the old search is for the entire reservation type and the new grouppoollid will already be in the old result.
        if (payload.oldGrouppoollid && payload.oldGrouppoollid !== payload.grouppoollid) {
            forceNewSearch = true;
            changedValues.push("grouppoollid");
        }

        // Check if pricecode have been changed and trigger a reset/new search.
        if (payload.oldPricecode !== payload.pricecode) {
            forceNewSearch = true;
            changedValues.push("pricecode");
        }

        // Check if logged in status has changed and trigger as reset/new search.
        if (typeof payload.oldIsLoggedIn !== "undefined" && payload.oldIsLoggedIn !== payload.isLoggedIn) {
            forceNewSearch = true;
            changedValues.push("isLoggedIn");
        }

        if (!forceNewSearch) {
            // This is an important trigger that we sometimes may want to trigger a new search.
            // Check first if we are allowed to make a new search for the given reservation type.
            // This check exists because we want to limit the number of searches to as few as possible per customer.
            // This will improve the booking experience!
            const defaultLifetime = state.search.defaultLifetime;
            const customLifetimes = state.search.customLifetimes;
            const now = moment();
            let lastSearched,
                lifetime = 0,
                allowSearch = false,
                hasSearchExpired = false;

            if (
                resvtypelid in state.search.lastSearched &&
                typeof state.search.lastSearched[resvtypelid] === "string"
            ) {
                lastSearched = moment(state.search.lastSearched[resvtypelid]);
            }

            if (
                typeof customLifetimes[reservationType.type] === "number" &&
                customLifetimes[reservationType.type] >= 0
            ) {
                lifetime = customLifetimes[reservationType.type];
            } else {
                lifetime = defaultLifetime;
            }

            if (lifetime === 0) {
                allowSearch = true;
            } else if (!lastSearched) {
                allowSearch = true;
            } else {
                // Compare last searched timestamps to see if a new search is allowed
                hasSearchExpired = lastSearched.clone().add(lifetime, "minutes").isBefore(now);

                if (hasSearchExpired) {
                    allowSearch = true;
                }
            }

            if (!allowSearch) {
                // if (isReset) {
                //     removeResetSearch();
                // }
                return;
            }
        } else if (!doPreFetchOnly) {
            dispatch(resetLastSearchedTimestampByResvtype(resvtypelid));
        }

        // Provide token if logged in as owner to identify the owners own accommodations.
        const config = state.account?.user?.isOwner
            ? {
                  headers: {
                      Authorization: `Bearer ${state.account.token}`,
                  },
              }
            : {};

        const searchMatchesPreFetchedResult = () => {
            if (!preFetchResult) {
                return false;
            }

            if (preFetchResult.search?.type !== resvtypelid) {
                return false;
            }

            if (preFetchResult.search?.arrdate !== searchParams.startdate) {
                return false;
            }

            if (preFetchResult.search?.depdate !== searchParams.enddate) {
                return false;
            }

            if ((preFetchResult.search?.promoCode?.toLowerCase() ?? "") !== promoCode.toLowerCase()) {
                return false;
            }

            if (
                preFetchResult.search?.guests &&
                searchParams.ages &&
                !arraysAreEqualAfterSort(preFetchResult.search.guests, searchParams.ages)
            ) {
                return false;
            }

            if (preFetchResult.search?.isLoggedIn !== isLoggedIn) {
                return false;
            }

            return true;
        };

        const updateReservationTypeResults = data => {
            // if (isReset) {
            //     dispatch(resetLastSearchedTimestampByResvtype(resvtypelid));
            //     dispatch(resetLastSearchedDateSpan());
            //     removeResetSearch();
            // } else {
            //     dispatch(updateLastSearchedTimestamp(resvtypelid));
            // }

            dispatch(updateLastSearchedTimestamp(resvtypelid));

            dispatch(setResultSetId(data.resultSetId, resvtypelid));

            dispatch(
                setReservationTypeResults(
                    data,
                    reservationType,
                    promoCode,
                    checkPriceAndAvailability,
                    payload.grouppoollid,
                    payload.pricecode,
                    isInitialSearch,
                    isLoggedIn,
                    false // Updates reservationResult
                )
            );

            if (
                !useAutoSearch &&
                checkPriceAndAvailability &&
                searchParams.startdate &&
                searchParams.enddate &&
                searchParams.startdate !== searchParams.enddate
            ) {
                dispatch(setLastSearchedDateSpan(resvtypelid, searchParams.startdate, searchParams.enddate));
            }
        };

        if (!doPreFetchOnly && searchMatchesPreFetchedResult()) {
            // If the search matches a preFetched result, use the preFetched result instead of making a new search.
            updateReservationTypeResults(preFetchResult);
            return;
        } else if (doPreFetchOnly) {
            dispatch(
                setRequestsLoadingByResvType({
                    request: "preFetchReservationTypeResults",
                    resvTypeId: resvtypelid,
                    loading: true,
                })
            );
        } else {
            dispatch(
                setRequestsLoadingByResvType({
                    request: "fetchReservationTypeResults",
                    resvTypeId: resvtypelid,
                    loading: true,
                })
            );
        }

        // Validate promo code and set promoCode to empty string if invalid.
        if (validatePromoCode) {
            try {
                const response = await AxiosClient.post("/promotions", { promoCode });

                if (response) {
                    promoCode = response.data.payload.promotionCode;
                    dispatch(setPromoCodeError(resvtypelid, false));
                }
            } catch (error) {
                promoCode = "";
                dispatch(setPromoCodeError(resvtypelid, true));
                removePromoCode();

                // If only the promo code has changed, we do not want to make a new search when the code is invalid.
                if (!doPreFetchOnly && changedValues.includes("promoCode") && changedValues.length === 1) {
                    dispatch(
                        setRequestsLoadingByResvType({
                            request: "fetchReservationTypeResults",
                            resvTypeId: resvtypelid,
                            loading: false,
                        })
                    );

                    return;
                }
            }
        }

        // Check if there is an existing request pending (to the same reservation type) and cancel it.
        if (controller && prevResvTypeLid === resvtypelid) {
            controller.abort();
        }

        // Create a new AbortController instance for the new request.
        controller = new AbortController();
        prevResvTypeLid = resvtypelid;

        // Optimzation: Do not run all search/results/criterias requests in parallell to give the API some time to handle them better.
        // 1. Search for products and return results directly in the same response
        // 2. Fetch criterias for results

        AxiosClient.get(`/search?resvtype=${resvtypelid}`, {
            ...config,
            params: searchParams,
            signal: controller.signal,
        })
            .then(searchResponse => {
                // const resultSetId = searchResponse.data.payload.resultset;

                if (doPreFetchOnly) {
                    // If doPreFetchOnly, update the preFetchResult for the reservation type.
                    dispatch(
                        setReservationTypeResults(
                            searchResponse.data.payload,
                            reservationType,
                            promoCode,
                            checkPriceAndAvailability,
                            payload.grouppoollid,
                            payload.pricecode,
                            isInitialSearch,
                            isLoggedIn,
                            true // Updates preFetchResult
                        )
                    );

                    dispatch(
                        setRequestsLoadingByResvType({
                            request: "preFetchReservationTypeResults",
                            resvTypeId: resvtypelid,
                            loading: false,
                        })
                    );
                } else {
                    // If not doPreFetchOnly, update the reservationResult for the reservation type.
                    updateReservationTypeResults(searchResponse.data.payload);
                    dispatch(
                        setRequestsLoadingByResvType({
                            request: "fetchReservationTypeResults",
                            resvTypeId: resvtypelid,
                            loading: false,
                        })
                    );
                }
            })
            .catch(catchSearchErrors);
    };
};

//AdditionalItem
export const fetchAdditionalItemInformation = payload => {
    const { resvtype, resultsetid, itemId, groupId, skipSystemPoolExtras = false } = payload;

    // Include resultset in URL only for debugging and track requests in log rocket
    const productsUrl = `/products?resultsetid=${resultsetid}&id=${itemId}`;

    return (dispatch, getState) => {
        const state = getState();

        // Provide token if logged in as owner to identify the owners own accommodations.
        const config = state.account?.user?.isOwner
            ? {
                  headers: {
                      Authorization: `Bearer ${state.account.token}`,
                  },
              }
            : {};

        dispatch(setRequestsLoading({ request: "fetchAdditionalItemInformation", loading: true }));

        AxiosClient.get(productsUrl, config)
            .then(response => {
                dispatch(setAdditionalItemInformation(response.data.payload, groupId, resvtype));

                let extras = response.data.payload.extras || [];

                if (skipSystemPoolExtras) {
                    extras = extras.filter(extra => !isSystemPool(extra));
                }
                dispatch(setRequestsLoading({ request: "fetchAdditionalItemInformation", loading: false }));
                dispatch(setCartProductExtras(itemId, extras));
            })
            .catch(error => {
                dispatch(fetchAdditionalItemInformationFailed(error, resvtype));
            });
    };
};

const fetchAdditionalItemInformationFailed = (error, resvtype) => {
    return {
        type: types.FETCH_ADDITIONALINITEMFORMATION_FAILED,
        error: error,
        resvtype: resvtype,
    };
};

const setAdditionalItemInformation = (payload, groupId, resvtype) => {
    // console.log('setAdditionalItemInformation', payload, groupId, resvtype);
    return {
        type: types.SET_ADDITIONALINITEMFORMATION,
        payload: payload,
        groupId: groupId,
        resvtype: resvtype,
    };
};

export const fetchProductsExtras = (reservationTypeId, productIds) => {
    return (dispatch, getState) => {
        const state = getState();

        // Provide token if logged in as owner to identify the owners own accommodations.
        const config = state.account?.user?.isOwner
            ? {
                  headers: {
                      Authorization: `Bearer ${state.account.token}`,
                  },
              }
            : {};

        dispatch(setRequestsLoading({ request: "fetchProductsExtras", loading: true }));

        const productsExtrasUrl = `/products/extras?ids=${productIds.join(",")}`;

        AxiosClient.get(productsExtrasUrl, config)
            .then(response => {
                dispatch(setRequestsLoading({ request: "fetchProductsExtras", loading: false }));
                dispatch(setProductsExtras(reservationTypeId, response.data.payload));
            })
            .catch(error => {
                dispatch(fetchProductsExtrasFailed(error, reservationTypeId));
            });
    };
};

const setProductsExtras = (reservationTypeId, productsExtras) => {
    return {
        type: types.SET_PRODUCTS_EXTRAS,
        payload: productsExtras,
        resvtype: reservationTypeId,
    };
};

const fetchProductsExtrasFailed = (error, resvtype) => {
    return {
        type: types.FETCH_PRODUCTS_EXTRAS_FAILED,
        error: error,
        resvtype: resvtype,
    };
};

export const setPreFetchUrl = (url, resvtype) => {
    return {
        type: types.SET_PREFETCH_URL,
        url,
        resvtype: resvtype,
    };
};

export const clearPreFetch = resvtype => {
    return {
        type: types.CLEAR_PREFETCH,
        resvtype: resvtype,
    };
};

export const setPromoCodeError = (resvtype, error) => {
    return {
        type: types.SET_PROMO_CODE_ERROR,
        resvtype: resvtype,
        error: error,
    };
};
